import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FirstStep from './RequestForProjectsPhases/FirstStep';
import SecondStep from './RequestForProjectsPhases/SecondStep';
import ThirdStep from './RequestForProjectsPhases/ThirdStep';
import FourthStep from './RequestForProjectsPhases/FourthStep';
import { createProposalProject } from 'ProposalProject/ProposalProject/Domain/Services/CreateProposalProjectService';
import { useParams } from 'react-router-dom';
import { getProposalProject } from 'ProposalProject/ProposalProject/Domain/Services/GetProposalProjectService';
import { getDocument } from 'Document/Domain/Services/GetDocumentService';
import { getProposalProjectAffectionDepartment } from 'ProposalProject/ProposalProjectAffectionDepartment/Domain/Services/GetProposalProjectAffectionDepartmentService';
import { getProposalProjectObjective } from 'ProposalProject/ProposalProjectObjective/Domain/Services/GetProposalProjectObjectiveService';
import { getProposalProjectObjectiveOperational } from 'ProposalProject/ProposalProjectObjectiveOperational/Domain/Services/GetProposalProjectObjectiveOperationalService';
import { getProposalProjectSavingsStaff } from 'ProposalProject/ProposalProjectSavingsStaff/Domain/Services/GetProposalProjectSavingsStaffService';
import { getProposalProjectExternalCost } from 'ProposalProject/ProposalProjectExternalCost/Domain/Services/GetProposalProjectExternalCostService';
import { getProposalProjectCostIT } from 'ProposalProject/ProposalProjectCostIT/Domain/Services/GetProposalProjectCostITService';
import { getProposalProjectRoi } from 'ProposalProject/ProposalProjectRoi/Domain/Services/GetProposalProjectRoiService';
import { ProposalProjectConstants } from 'ProposalProject/ProposalProject/Domain/Model/ProposalProjectConstant';
import Loader from 'UI/Application/Components/Loaders/Loader';

const FormProposalProjectView = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [idProject, setIdProject] = useState(id);
    const [loadData, setLoadData] = useState(true);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('');
    const [documents, setDocuments] = useState([]);
    const [affectionRows, setAffectionRows] = useState([]);
    const [currentStep, setCurrentStep] = useState(1); // 1 para FirstStep, 2 para SecondStep
    const [objectives, setObjectives] = useState([]);
    const [objectivesOperational, setObjectivesOperational] = useState([]);
    const [savingsStaff, setSavingsStaff] = useState([]);
    const [externalCost, setExternalCost] = useState([]);
    const [costIT, setCostIT] = useState([]);
    const [roi, setRoi] = useState([]);
    const [actualStatus, setActualStatus] = useState(''); // To track the actual workflow status
    const [userOwner, setUserOwner] = useState(null);
    const [jiraEpicKey, setJiraEpicKey] = useState(null); // Add state for jiraEpicKey

    useEffect(() => {
        const fetchData = async () => {                
            if (idProject === "new") {
                setDocuments([]);
                try {
                    const apiResponse = await createProposalProject({
                        title: '',
                        description: '',
                    });
                    if (apiResponse.id) {
                        setIdProject(apiResponse.id);
                        setStatus(apiResponse.status);
                        window.history.pushState({}, '', window.location.href.replace('/new', '/'+apiResponse.id));
                        window.location.reload(); // Fuerzo la recarga para que se cargue el nuevo proyecto correctamente
                    }
                } catch (error) {
                    //console.error("Error creating new proposal project:", error);
                }
            } else {
                try {
                    const response = await getProposalProject(idProject);
                    if (response) {
                        setIdProject(response.id);
                        setTitle(response.title);
                        setDescription(response.description);
                        setStatus(response.status);
                        setUserOwner(response.userOwnerId);
                        setJiraEpicKey(response.jiraEpicKey); // Store jiraEpicKey
                    }
                } catch (error) {
                    //console.error("Error fetching proposal project:", error);
                }

                try {
                    const response = await getDocument(idProject, 'ProposalProject');
                    if (response) {
                        setDocuments(response);
                    }
                } catch (error) {
                    //console.error("Error fetching documents:", error);
                }

                try {
                    const response = await getProposalProjectAffectionDepartment(idProject);
                    if (response) {
                        setAffectionRows(response);
                    } else {
                        setAffectionRows([]);
                    }
                } catch (error) {
                    //console.error("Error fetching affection departments:", error);
                }

                try{
                    const response = await getProposalProjectObjective(idProject);
                    if (response) {
                        setObjectives(response);
                    } else {
                        setObjectives([]);
                    }
                }
                catch(error){
                    //console.error("Error fetching objectives:", error);
                }

                try{
                    const response = await getProposalProjectObjectiveOperational(idProject);
                    if (response) {
                        setObjectivesOperational(response);
                    } else {
                        setObjectivesOperational([]);
                    }
                }
                catch(error){
                    //console.error("Error fetching operational objectives:", error);
                }

                try{
                    const response = await getProposalProjectSavingsStaff(idProject);
                    if (response) {
                        setSavingsStaff(response);
                    }                    
                }
                catch(error){
                    //console.error("Error fetching savings staff:", error);
                }

                try{
                    const response = await getProposalProjectExternalCost(idProject);
                    if (response) {
                        setExternalCost(response);
                    }                    
                }
                catch(error){
                    console.error("Error fetching external costs:", error);
                }

                try{
                    const response = await getProposalProjectCostIT(idProject);
                    if (response) {
                        setCostIT(response);
                    }                    
                }
                catch(error){
                    // console.error("Error fetching IT costs:", error);
                }

                try{
                    const response = await getProposalProjectRoi(idProject);
                    if (response) {
                        setRoi(response);
                    }
                }
                catch(error){
                    // console.error("Error fetching ROI:", error);
                }            

            }
        };
        if (loadData) {
            fetchData();
            setLoadData(false);
        }
    }, [idProject, loadData]);

    useEffect(() => {
        // Store the actual status
        setActualStatus(status);
        
        switch (status) {
            case ProposalProjectConstants.STATUS_STEP1:
                setCurrentStep(1);
                break;
            case ProposalProjectConstants.STATUS_STEP2:
                setCurrentStep(2);
                break;
            case ProposalProjectConstants.STATUS_STEP3:
                setCurrentStep(3);
                break;
            case ProposalProjectConstants.STATUS_STEP4:
            case ProposalProjectConstants.STATUS_STANDBY:
            case ProposalProjectConstants.STATUS_ACCEPTED:
            case ProposalProjectConstants.STATUS_REFUSED:
            case ProposalProjectConstants.STATUS_FINISHED:
                setCurrentStep(4);
                break;
            default:
                setCurrentStep(1);
                break;
        }
    }, [status]);

    const h1Styles = {
        textAlign: 'center',
    };

    // Function to handle navigation between steps
    const navigateToStep = (step) => {
        setCurrentStep(step);
    };

    // Check if a step is editable based on current status
    const isStepEditable = (step) => {
        switch (step) {
            case 1:
                return actualStatus === ProposalProjectConstants.STATUS_STEP1;
            case 2:
                return actualStatus === ProposalProjectConstants.STATUS_STEP2;
            case 3:
                return actualStatus === ProposalProjectConstants.STATUS_STEP3;
            case 4:
                return actualStatus === ProposalProjectConstants.STATUS_STEP4 || actualStatus === ProposalProjectConstants.STATUS_ACCEPTED || actualStatus === ProposalProjectConstants.STATUS_STANDBY;
            default:
                return false;
        }
    };

    const reloadData = () => {
        setLoadData(true);
    };

    // Step navigation component
    const StepNavigation = () => {
        const getStepStyle = (step) => ({
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: '2px solid #000',
            color: step === currentStep ? '#fff' : '#000',
            backgroundColor: step === currentStep ? '#0492D4' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            cursor: 'pointer',
        });

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={getStepStyle(1)} onClick={() => navigateToStep(1)}>1</div>
                    <div style={{ margin: '0 10px' }}>→</div>
                    <div style={getStepStyle(2)} onClick={() => navigateToStep(2)}>2</div>
                    <div style={{ margin: '0 10px' }}>→</div>
                    <div style={getStepStyle(3)} onClick={() => navigateToStep(3)}>3</div>
                    <div style={{ margin: '0 10px' }}>→</div>
                    <div style={getStepStyle(4)} onClick={() => navigateToStep(4)}>4</div>
                </div>
            </div>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <h1 style={h1Styles}>{t('ui.menus.user.proposalProject')}</h1>
            {idProject === "new" || status === '' ? (
                <Loader />
            ):(
                <>
                    <StepNavigation />
                    
                    {currentStep === 1 ? (
                        <FirstStep
                            idProposalProject={idProject}
                            titleProposalProject={title}
                            descriptionProposalProject={description}
                            statusProposalProject={status}
                            documentsProposalProject={documents}
                            userOwnerProposalProject={userOwner}
                            affectionRowsProposalProject={affectionRows}
                            objectivesProposalProject={objectives}
                            objectivesOperationalProposalProject={objectivesOperational}
                            reloadData={reloadData}
                            readOnly={!isStepEditable(1)}
                            currentStatus={actualStatus}
                        />
                    ) : currentStep === 2 ? (
                        <SecondStep 
                            idProposalProject={idProject}
                            savingsStaffProposalProject={savingsStaff}
                            externalCostProposalProject={externalCost}
                            reloadData={reloadData}
                            readOnly={!isStepEditable(2)}
                            currentStatus={actualStatus}
                        />
                    ) : currentStep === 3 ? (
                        <ThirdStep 
                            idProposalProject={idProject}
                            savingsStaffProposalProject={savingsStaff}
                            costITProposalProject={costIT}
                            reloadData={reloadData}
                            readOnly={!isStepEditable(3)}
                            currentStatus={actualStatus}
                        />
                    ) : currentStep === 4 ? (
                        <FourthStep 
                            idProposalProject={idProject}
                            proposalProjectStatus={status}
                            roiProposalProject={roi}
                            reloadData={reloadData}
                            readOnly={!isStepEditable(4)}
                            currentStatus={actualStatus}
                            jiraEpicKey={jiraEpicKey} // Pass jiraEpicKey to FourthStep
                        />
                    )
                    : null}
                </>
            )}
        </Box>
    );
};

export default FormProposalProjectView;